import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Shield, Smartphone, QrCode, Key, CheckCircle2, Copy, AlertCircle } from 'lucide-react';
import { useNavigation } from '../context/NavigationContext';
import { Screen } from '../types';
import { Header } from '../components/ui/Header';
import { Input } from '../components/ui/Input';
import { Button } from '../components/ui/Button';
export function TwoFactorScreen() {
  const {
    goBack
  } = useNavigation();
  const [isEnabled, setIsEnabled] = useState(false);
  const [step, setStep] = useState(0); // 0: intro, 1: setup, 2: verify, 3: success
  const [verifyCode, setVerifyCode] = useState('');
  const [showCopied, setShowCopied] = useState(false);
  const backupCodes = ['8472-9103', '5610-2948', '1039-4827', '9284-1038', '3847-1928', '5729-1048', '2948-5710', '1048-2938'];
  const handleCopyCodes = () => {
    // navigator.clipboard.writeText(backupCodes.join('\n'))
    setShowCopied(true);
    setTimeout(() => setShowCopied(false), 2000);
  };
  const handleVerify = () => {
    if (verifyCode.length === 6) {
      setStep(3);
      setIsEnabled(true);
    }
  };
  const Toggle = ({
    enabled,
    onChange
  }: {
    enabled: boolean;
    onChange: () => void;
  }) => <button onClick={onChange} className={`w-12 h-6 rounded-full transition-colors relative ${enabled ? 'bg-primary' : 'bg-gray-200'}`} data-id="element-1459">
      <div className={`w-5 h-5 bg-white rounded-full absolute top-0.5 transition-transform shadow-sm ${enabled ? 'translate-x-6' : 'translate-x-0.5'}`} data-id="element-1460" />
    </button>;
  return <motion.div initial={{
    opacity: 0,
    x: 20
  }} animate={{
    opacity: 1,
    x: 0
  }} exit={{
    opacity: 0,
    x: -20
  }} className="min-h-screen bg-gray-50 pb-24" data-id="element-1461">
      <Header title="Two-Factor Authentication" data-id="element-1462" />

      <div className="p-4 space-y-4" data-id="element-1463">
        {/* Main Toggle Card */}
        <div className="bg-white p-5 rounded-2xl shadow-sm border border-gray-50" data-id="element-1464">
          <div className="flex items-center justify-between mb-4" data-id="element-1465">
            <div className="flex items-center gap-3" data-id="element-1466">
              <div className={`w-10 h-10 rounded-full flex items-center justify-center ${isEnabled ? 'bg-success/10 text-success' : 'bg-gray-100 text-gray-500'}`} data-id="element-1467">
                <Shield className="w-5 h-5" data-id="element-1468" />
              </div>
              <div data-id="element-1469">
                <h2 className="font-bold text-gray-900" data-id="element-1470">Authenticator App</h2>
                <p className="text-xs text-gray-500" data-id="element-1471">
                  {isEnabled ? 'Enabled' : 'Not configured'}
                </p>
              </div>
            </div>
            <Toggle enabled={isEnabled || step > 0} onChange={() => {
            if (isEnabled) {
              setIsEnabled(false);
              setStep(0);
            } else {
              setStep(1);
            }
          }} data-id="element-1472" />
          </div>

          <p className="text-sm text-gray-600 leading-relaxed" data-id="element-1473">
            Add an extra layer of security to your account. Once enabled, you'll
            be required to enter both your password and an authentication code
            from your mobile app.
          </p>
        </div>

        {/* Setup Flow */}
        <AnimatePresence mode="wait" data-id="element-1474">
          {step === 1 && <motion.div key="step1" initial={{
          opacity: 0,
          height: 0
        }} animate={{
          opacity: 1,
          height: 'auto'
        }} exit={{
          opacity: 0,
          height: 0
        }} className="bg-white p-5 rounded-2xl shadow-sm border border-gray-50 space-y-6 overflow-hidden" data-id="element-1475">
              <div className="text-center" data-id="element-1476">
                <h3 className="font-bold text-gray-900 mb-2" data-id="element-1477">
                  Step 1: Scan QR Code
                </h3>
                <p className="text-sm text-gray-500 mb-6" data-id="element-1478">
                  Open your authenticator app (like Google Authenticator or
                  Authy) and scan this QR code.
                </p>

                <div className="w-48 h-48 mx-auto bg-gray-50 rounded-xl border-2 border-dashed border-gray-200 flex items-center justify-center mb-4 relative overflow-hidden" data-id="element-1479">
                  <QrCode className="w-32 h-32 text-gray-800" data-id="element-1480" />
                  {/* Decorative scan line */}
                  <motion.div animate={{
                y: [-90, 90]
              }} transition={{
                repeat: Infinity,
                duration: 2,
                ease: 'linear'
              }} className="absolute w-full h-0.5 bg-primary shadow-[0_0_8px_2px_rgba(20,113,143,0.5)]" data-id="element-1481" />
                </div>

                <div className="bg-gray-50 p-3 rounded-xl border border-gray-100" data-id="element-1482">
                  <p className="text-xs text-gray-500 mb-1" data-id="element-1483">
                    Or enter this code manually:
                  </p>
                  <p className="font-mono font-bold text-gray-900 tracking-widest" data-id="element-1484">
                    JUMM ALLS ECUR E123
                  </p>
                </div>
              </div>

              <Button fullWidth onClick={() => setStep(2)} data-id="element-1485">
                I've scanned the code
              </Button>
            </motion.div>}

          {step === 2 && <motion.div key="step2" initial={{
          opacity: 0,
          x: 20
        }} animate={{
          opacity: 1,
          x: 0
        }} exit={{
          opacity: 0,
          x: -20
        }} className="bg-white p-5 rounded-2xl shadow-sm border border-gray-50 space-y-6" data-id="element-1486">
              <div className="text-center" data-id="element-1487">
                <div className="w-16 h-16 bg-primary-50 rounded-full flex items-center justify-center mx-auto mb-4 text-primary" data-id="element-1488">
                  <Smartphone className="w-8 h-8" data-id="element-1489" />
                </div>
                <h3 className="font-bold text-gray-900 mb-2" data-id="element-1490">
                  Step 2: Verify Code
                </h3>
                <p className="text-sm text-gray-500 mb-6" data-id="element-1491">
                  Enter the 6-digit code generated by your authenticator app to
                  verify the setup.
                </p>

                <Input placeholder="000 000" value={verifyCode} onChange={e => setVerifyCode(e.target.value.replace(/\D/g, '').slice(0, 6))} className="text-center text-2xl tracking-[0.5em] font-mono py-4" inputMode="numeric" data-id="element-1492" />
              </div>

              <Button fullWidth onClick={handleVerify} disabled={verifyCode.length !== 6} data-id="element-1493">
                Verify & Enable
              </Button>
            </motion.div>}

          {step === 3 && <motion.div key="step3" initial={{
          opacity: 0,
          scale: 0.95
        }} animate={{
          opacity: 1,
          scale: 1
        }} className="space-y-4" data-id="element-1494">
              <div className="bg-success/10 border border-success/20 p-5 rounded-2xl flex flex-col items-center text-center" data-id="element-1495">
                <CheckCircle2 className="w-12 h-12 text-success mb-3" data-id="element-1496" />
                <h3 className="font-bold text-gray-900 mb-1" data-id="element-1497">
                  Two-Factor Enabled!
                </h3>
                <p className="text-sm text-gray-600" data-id="element-1498">
                  Your account is now protected with an extra layer of security.
                </p>
              </div>

              <div className="bg-white p-5 rounded-2xl shadow-sm border border-gray-50" data-id="element-1499">
                <div className="flex items-center gap-2 mb-4" data-id="element-1500">
                  <Key className="w-5 h-5 text-primary" data-id="element-1501" />
                  <h3 className="font-bold text-gray-900" data-id="element-1502">Backup Codes</h3>
                </div>

                <div className="bg-warning/10 border border-warning/20 p-3 rounded-xl flex items-start gap-3 mb-4" data-id="element-1503">
                  <AlertCircle className="w-5 h-5 text-warning shrink-0 mt-0.5" data-id="element-1504" />
                  <p className="text-xs text-warning-800 leading-relaxed" data-id="element-1505">
                    Save these backup codes in a secure place. You can use them
                    to access your account if you lose your phone.
                  </p>
                </div>

                <div className="grid grid-cols-2 gap-3 mb-4" data-id="element-1506">
                  {backupCodes.map((code, idx) => <div key={idx} className="bg-gray-50 py-2 px-3 rounded-lg border border-gray-100 text-center font-mono text-sm tracking-wider text-gray-700" data-id="element-1507">
                      {code}
                    </div>)}
                </div>

                <Button variant="outline" fullWidth icon={<Copy className="w-4 h-4" data-id="element-1509" />} onClick={handleCopyCodes} data-id="element-1508">
                  {showCopied ? 'Copied!' : 'Copy Codes'}
                </Button>
              </div>
            </motion.div>}
        </AnimatePresence>
      </div>
    </motion.div>;
}